import React, { useEffect, useState } from 'react';
import { generatePath } from 'react-router';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  ExperimentState,
  IPlaybookBankAnnotated,
  playbookBankService,
  RunningDashboard,
  runningDashboardService,
  RunningExperimentSummary,
} from 'src/services';
import { useAppDispatch } from 'src/hooks';
import { formatCompactNumber } from 'src/utils';
import { appHeaderActions } from 'src/containers/AppHeader/Actions';
import { PlaybookBankServiceLike, PlaybookCard } from './PlaybookCard';
import { PlaybooksGrid } from './PlaybookCard/PlaybookCard.styles';
import {
  DayBlock,
  DayLabel,
  EmptyStateIntro,
  ErrorState,
  ExperimentLink,
  ExperimentMeta,
  ExperimentMetricLabel,
  ExperimentNameBlock,
  ExperimentRow,
  FooterLink,
  LoadingState,
  StatusText,
  VariantTable,
  VisuallyHidden,
  WidgetCard,
  WidgetFooter,
  WidgetHeader,
  WidgetHeaderIcon,
  WidgetTitle,
} from './ExperimentsHomeWidget.styles';

interface RunningDashboardServiceLike {
  getRunningExperimentsDashboard(args: { shopId: number }): Promise<RunningDashboard>;
}

interface PlaybookBankFeaturedServiceLike extends PlaybookBankServiceLike {
  getFeaturedPlaybooks(args: { shopId: number }): Promise<IPlaybookBankAnnotated[]>;
}

interface ExperimentsHomeWidgetProps {
  shopId: number;
  experimentsRoute: string | undefined;
  abTestDashboardRoute: string | undefined;
  experimentWizardRoute?: string;
  dataFieldsRoute?: string;
  service?: RunningDashboardServiceLike;
  playbookService?: PlaybookBankFeaturedServiceLike;
}

const METRIC_CONFIG: Record<string, { label: string; format: (v: number) => string }> = {
  user_conversion: {
    label: 'CVR',
    format: (v: number) => `${(v * 100).toFixed(2)}%`,
  },
  average_order_value: {
    label: 'AOV',
    format: (v: number) => `$${v.toFixed(2)}`,
  },
  average_revenue_per_user: {
    label: 'ARPU',
    format: (v: number) => `$${v.toFixed(2)}`,
  },
};

function getMetricConfig(metric: string): { label: string; format: (v: number) => string } {
  return (
    METRIC_CONFIG[metric] ?? {
      label: metric,
      format: (v: number) => String(v),
    }
  );
}

function getMetricValue(
  experiment: RunningExperimentSummary,
  variantUserCvr: number,
  variantAov: number,
  variantArpu: number
): number {
  switch (experiment.metric) {
    case 'user_conversion':
      return variantUserCvr;
    case 'average_order_value':
      return variantAov;
    case 'average_revenue_per_user':
      return variantArpu;
    default:
      return variantUserCvr;
  }
}

const WINNER_STATES = new Set<ExperimentState>(['winner_found', 'ended_winner']);

export const ExperimentsHomeWidget = ({
  shopId,
  experimentsRoute,
  abTestDashboardRoute,
  experimentWizardRoute,
  dataFieldsRoute,
  service = runningDashboardService,
  playbookService = playbookBankService,
}: ExperimentsHomeWidgetProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<RunningDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playbooks, setPlaybooks] = useState<IPlaybookBankAnnotated[]>([]);
  const [playbooksLoaded, setPlaybooksLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    service
      .getRunningExperimentsDashboard({ shopId })
      .then(result => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load running experiments.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shopId, service]);

  const experiments = data?.experiments ?? [];
  const hasExperiments = experiments.length > 0;
  const primaryExperiment = experiments[0];

  useEffect(() => {
    if (loading || hasExperiments) {
      return undefined;
    }

    let cancelled = false;
    setPlaybooksLoaded(false);

    playbookService
      .getFeaturedPlaybooks({ shopId })
      .then(result => {
        if (!cancelled) {
          setPlaybooks(result ?? []);
          setPlaybooksLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlaybooks([]);
          setPlaybooksLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shopId, playbookService, loading, hasExperiments]);

  const handleNavigate = (path: string) => {
    dispatch(appHeaderActions.navigateTo({ navigateTo: path }));
  };

  const getExperimentPath = (experimentId: string): string =>
    abTestDashboardRoute
      ? generatePath(abTestDashboardRoute, { shopId, experimentId })
      : `/shops/${shopId}/ab-test-dashboard/${experimentId}`;

  const activeFooterLabel =
    experiments.length > 1 ? `View all (${experiments.length})` : 'View all Experiments';

  const renderActiveState = () => {
    const exp = primaryExperiment;
    const metricConfig = getMetricConfig(exp.metric);
    const isSignificant = WINNER_STATES.has(exp.status);

    return (
      <>
        <ExperimentRow>
          <ExperimentNameBlock>
            <ExperimentLink
              href={getExperimentPath(exp.experimentId)}
              onClick={e => {
                e.preventDefault();
                handleNavigate(getExperimentPath(exp.experimentId));
              }}
            >
              {exp.experimentName}
            </ExperimentLink>
            <ExperimentMetricLabel>({metricConfig.label})</ExperimentMetricLabel>
          </ExperimentNameBlock>
          <ExperimentMeta>
            <HourglassEmptyOutlinedIcon />
            <DayBlock>
              <DayLabel>Day {exp.daysInTest}</DayLabel>
              <StatusText>{isSignificant ? 'Significant' : 'Not yet significant'}</StatusText>
            </DayBlock>
          </ExperimentMeta>
        </ExperimentRow>

        {(exp.variants?.length ?? 0) > 0 && (
          <VariantTable>
            <thead>
              <tr>
                <th>Variant</th>
                <th>Sample Size</th>
                <th>{metricConfig.label}</th>
              </tr>
            </thead>
            <tbody>
              {exp.variants?.map(v => (
                <tr key={v.variantName}>
                  <td className='var'>
                    {v.variantName}
                    {v.isWinner && (
                      <span className='winner-icon'>
                        {' '}
                        <span aria-hidden='true'>🏆</span>
                        <VisuallyHidden>Winner</VisuallyHidden>
                      </span>
                    )}
                  </td>
                  <td className='num'>{formatCompactNumber(v.users)}</td>
                  <td className='num'>
                    {metricConfig.format(getMetricValue(exp, v.userCvr, v.aov, v.arpu))}
                  </td>
                </tr>
              ))}
            </tbody>
          </VariantTable>
        )}

        {experimentsRoute && (
          <WidgetFooter>
            <FooterLink
              href={experimentsRoute}
              onClick={e => {
                e.preventDefault();
                handleNavigate(experimentsRoute);
              }}
            >
              {activeFooterLabel} <ArrowForwardIcon />
            </FooterLink>
          </WidgetFooter>
        )}
      </>
    );
  };

  return (
    <WidgetCard>
      <WidgetHeader>
        <WidgetHeaderIcon>
          <ScienceOutlinedIcon />
        </WidgetHeaderIcon>
        <WidgetTitle>{hasExperiments ? 'Running Experiments' : 'Try an A/B test'}</WidgetTitle>
      </WidgetHeader>

      {loading && <LoadingState aria-live='polite'>Loading…</LoadingState>}

      {error && !loading && <ErrorState role='alert'>{error}</ErrorState>}

      {!loading && !error && hasExperiments && renderActiveState()}

      {!loading && !error && !hasExperiments && (
        <>
          <EmptyStateIntro>
            {playbooksLoaded && playbooks.length > 0
              ? 'There are no running experiments. Here are a few ideas to get started.'
              : 'There are no running experiments.'}
          </EmptyStateIntro>

          {playbooksLoaded && playbooks.length > 0 && (
            <PlaybooksGrid>
              {playbooks.slice(0, 3).map(playbook => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  shopId={shopId}
                  experimentWizardRoute={experimentWizardRoute}
                  dataFieldsRoute={dataFieldsRoute}
                  onNavigate={handleNavigate}
                  service={playbookService}
                />
              ))}
            </PlaybooksGrid>
          )}

          {experimentsRoute && (
            <WidgetFooter>
              <FooterLink
                href={experimentsRoute}
                onClick={e => {
                  e.preventDefault();
                  handleNavigate(experimentsRoute);
                }}
              >
                Run a new experiment <ArrowForwardIcon />
              </FooterLink>
            </WidgetFooter>
          )}
        </>
      )}
    </WidgetCard>
  );
};
